import { Alert, Box, Button, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { displayNotification, generateOptionsFromData, sendAPIRequest } from "helpers";
import DataTable from "pages/components/DataTable";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaList, FaPlus } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { FormDataContext } from "../../contexts/FormContext";
import PopupModal from "../../PopupModal";
import { Link } from "react-router-dom";
import { useGetListByMasterIdQuery } from "modules/pvs_activities/redux/service/lists";
import DropdownField from "../../core/DropdownField";
import { useGetCountriesQuery, useGetMissionCountriesQuery } from "modules/pvs_activities/redux/service/country";
import { useGetLanguagesQuery } from "modules/pvs_activities/redux/service/language";
import { useGetExpertNameQuery, useLazyGetExpertsQuery } from "modules/pvs_activities/redux/service/experts";
import _ from "lodash";
import { dFormApi, useGetExpertsFromMovementQuery } from "modules/pvs_activities/redux/service/dForm";

const initialFilters = {
    country_id: [],
    lang_id: [],
    expertise: [],
    expert_name: [],
    mission_country_id: [],
    country_of_residence: [],
    user_id: null,
};

const defaultProps = {
    id: "",
    element: {},
};

const ManagePeerReviewerModal = (rawProps) => {
    const props = { ...defaultProps, ...rawProps };

    const { t } = useTranslation();
    const formDataContext = useContext(FormDataContext);
    const authData = useSelector((s) => s.auth.data);
    const modalRef = useRef();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [experts, setExperts] = useState([]);
    const [previousMissionExperts, setPreviousMissionExperts] = useState([]);
    const [filters, setFilters] = useState({ ...initialFilters });
    const [data, setData] = useState({});
    const [expanded, setExpanded] = useState({});
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [MissionCountryName, setMissionCountryName] = useState('');
    const [expertise] = useState([
        { id: "pvs-expert", name: "PVS Expert" },
        { id: "aqua-expert", name: "Aqua Expert" },
        { id: "gap-expert", name: "GAP Expert" },
        { id: "legislation-expert", name: "Legislation Expert" },
        { id: "laboratory-expert", name: "Laboratory Expert" },
    ]);
    const positionsRes = useGetListByMasterIdQuery({ id: "17", params: { only_ids: 94 } });
    const countryList = useGetCountriesQuery();
    const missionCountryList = useGetMissionCountriesQuery();
    const languageList = useGetLanguagesQuery();
    const expertNames = useGetExpertNameQuery();
    const [fetchExperts] = useLazyGetExpertsQuery();
    const existingExpertsRes = useGetExpertsFromMovementQuery({
        movementId: formDataContext.movementId
    }, { skip: formDataContext.mode === "preview" });

    useEffect(() => {
        if (modalRef.current?.isOpen && existingExpertsRes.isSuccess) {
            loadExperts();
            loadPreviousMissionExperts();
        }
    }, [existingExpertsRes]);

    useEffect(() => {
        setFilters({
            ...filters,
            user_id: authData?.id
        });
    }, [authData]);

    useEffect(() => {
        if (positionsRes.isError) {
            displayNotification("error", "Error loading positions");
            console.error({ error: positionsRes.error });
        }
    }, [positionsRes]);

    useEffect(() => {
        if (languageList.isError) {
            displayNotification("error", "Error loading language List");
            console.error({ error: languageList.error });
        }
    }, [languageList]);

    useEffect(() => {
        if (expertNames.isError) {
            displayNotification("error", "Error loading expert names");
            console.error({ error: expertNames.error });
        }
    }, [expertNames]);

    useEffect(() => {
        if (missionCountryList.isError) {
            displayNotification("error", "Error loading missions countries");
            console.error({ error: missionCountryList.error });
        }
    }, [missionCountryList]);

    useEffect(() => {
        if (countryList.isError) {
            displayNotification("error", "Error loading countries");
            console.error({ error: countryList.error });
        }
    }, [countryList]);

    const loadExperts = async (chk = true) => {
        try {
            let fltr = { ...filters };
            if (chk === false) {
                fltr = initialFilters;
            }
            let temp = await fetchExperts({ params: fltr });
            if (temp.status === "fulfilled") {
                if (existingExpertsRes.isSuccess) {
                    const existingIds = existingExpertsRes.data.map(r => r.expert_id);
                    setExperts(temp.data.filter(r => !existingIds.includes(r.id)));
                } else {
                    setExperts([]);
                }
                setIsLoading(false);
            } else {
                displayNotification("error", `${t("-1", "Error loading experts data")} [B]`);
            }
        } catch (e) {
            console.error("Error fetching experts:", e);
            displayNotification("error", `${t("-1", "Error saving experts")} [F]`);
        }
    };

    const loadPreviousMissionExperts = async (chk = true) => {
        try {
            setIsLoading(true);
            const req = await sendAPIRequest(`/api/expert/load_experts?mission_id=${props.missionId}&select_expert_peereviewer=2`);
            const res = await req.json();
            if (res.success) {
                if (existingExpertsRes.isSuccess) {
                    const existingIds = existingExpertsRes.data.map(r => r.expert_id);
                    setPreviousMissionExperts(res.data.filter(r => !existingIds.includes(r.id)));
                } else {
                    setPreviousMissionExperts([]);
                }
                setMissionCountryName(res?.country) // for title
                setIsLoading(false);
            } else {
                displayNotification("error", `${t("-1", "Error loading previous mission experts data")} [B]`);
            }
        } catch (e) {
            console.error(e);
            displayNotification("error", `${t("-1", "Error loading previous mission experts data")} [F]`);
        }
    };

    const handleSave = async () => {
        try {
            if (!window.confirm("You are about to save data. Are you sure?")) {
                return;
            }
            if (Object.values(data).filter((v) => v === "").length > 0) {
                displayNotification("warning", t("-1", "Please select position for each selected expert"));
                return;
            }
            if (formDataContext.mode === "preview") {
                displayNotification("success", `${t("-1", "Successfully saved experts")} [B]`);
                return;
            }
            setIsSaving(true);
            const req = await sendAPIRequest(
                `/api/pvsa/dform/save-experts-from-movement/${formDataContext.movementId}`,
                "POST",
                {
                    entries: data,
                }
            );
            const res = await req.json();
            if (res.success) {
                dispatch(dFormApi.util.invalidateTags(['ExpertsFromMovement']));
                displayNotification("success", `${t("-1", "Successfully saved experts")}`);
                modalRef.current?.closeModal();
            } else {
                displayNotification("error", `${t("-1", "Error saving experts")} [B]`);
            }
            setIsSaving(false);
        } catch (e) {
            setIsSaving(false);
            console.error(e);
            displayNotification("error", `${t("-1", "Error saving experts")} [F]`);
        }
    };

    const handleRowSelection = useCallback(
        (newRowSelectionModel) => {
            const tmp = { ...data };
            for (const expId of newRowSelectionModel) {
                if (tmp[expId] === undefined) {
                    tmp[expId] = "94";
                }
            }
            setData(tmp);
            setRowSelectionModel([...newRowSelectionModel]);
        },
        [data]
    );

    const countryOptions = useMemo(() => {
        if (countryList.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (countryList.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(countryList.data, {
            value: "id",
            label: "country_complete_name",
        });
    }, [countryList]);

    const countryAgainstMissionOptions = useMemo(() => {
        if (missionCountryList.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (missionCountryList.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(missionCountryList.data, {
            value: "id",
            label: "country_name",
        });
    }, [missionCountryList]);

    const positionsOptions = useMemo(() => {
        if (positionsRes.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (positionsRes.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(positionsRes.data, {
            value: "id",
            label: "list_element_name",
        });
    }, [positionsRes]);

    const expertiseOptions = useMemo(() => {
        return generateOptionsFromData(expertise, {
            value: "id",
            label: "name",
        });
    }, [expertise]);

    const languageOptions = useMemo(() => {
        if (languageList.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (languageList.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(languageList.data, {
            value: "id",
            label: "language",
        });
    }, [languageList]);

    const expertNameOptions = useMemo(() => {
        if (expertNames.isLoading)
            return [
                {
                    value: "",
                    label: "Loading...",
                },
            ];
        if (expertNames.isError)
            return [
                {
                    value: "",
                    label: "Error Loading...",
                },
            ];
        return generateOptionsFromData(expertNames.data, {
            value: "person_id",
            label: "expert_name",
        });
    }, [expertNames]);

    const resetFilter = async () => {
        setFilters({ ...initialFilters });
        await loadExperts(false);
    };

    const navigationFunction = (key, value) => {
        let params = { ...filters };
        params[key] = value;
        setFilters({
            ...params,
        });
    };

    const MissionCellRenderer = ({ row }) => {
        const missions = Array.isArray(row?.mission) ? row.mission : [];
        const displayMissions = expanded[row.id] ? missions : missions.slice(0, 3);
        const toggleExpanded = (rowId) => { setExpanded(prevState => ({ ...prevState, [rowId]: !prevState[rowId] })) };
        return (
            <div className="row d-flex">
                {displayMissions.map((data, index) => (
                    <span key={index}>{data.name}{index < displayMissions.length - 1 && ' '}</span>
                ))}

                {missions.length > 3 && (<span className="btn-link cursor-pointer fst-italic" onClick={() => { toggleExpanded(row.id) }}>
                    {expanded[row.id] ? "Show Less..." : "Show More..."}
                </span>)}
            </div>
        )
    }
    return (
        <PopupModal
            ref={modalRef}
            title={"Add Peer Reviewers"}
            size={"xxl"}
            triggers={({openModal}) => (
                <Button variant={"contained"} color={"primary"} onClick={openModal} size="small">
                    <FaList />
                    <span className="ms-1">Add Peer Reviewers</span>
                </Button>
            )}
            onOpen={() => {
                loadExperts();
                loadPreviousMissionExperts();
            }}
        >
            <>
                <Card className="mb-2">
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", mb: 1 }}>
                            <Typography variant="h5" gutterBottom>
                                {t("14", "Filters")}
                            </Typography>
                            <Box sx={{ ml: "auto" }}>
                                <Link to={`/portal/experts-management/create`} target="_blank">
                                    <Button variant="contained" color="primary">
                                        <FaPlus />
                                        <span className="ms-1">Create New Expert</span>
                                    </Button>
                                </Link>
                            </Box>
                        </Box>
                        <div className="row">
                            <div className="col-md-3 col-12 mb-2">
                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                    <>
                                        <DropdownField
                                            label={t("2917", "Nationality")}
                                            hasOptions={true}
                                            options={countryOptions}
                                            plain={true}
                                            size={"small"}
                                            multiple={true}
                                            selectAll={true}
                                            value={filters.country_id ? filters.country_id : []}
                                            onChange={(value) => navigationFunction("country_id", value)}
                                        />
                                    </>
                                </Tooltip>
                            </div>
                            <div className="col-md-3 col-12 mb-2">
                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                    <>
                                        <DropdownField
                                            label={t("2887", "Country Missions")}
                                            hasOptions={true}
                                            options={countryAgainstMissionOptions}
                                            plain={true}
                                            size={"small"}
                                            multiple={true}
                                            selectAll={true}
                                            value={filters.mission_country_id ? filters.mission_country_id : []}
                                            onChange={(value) => navigationFunction("mission_country_id", value)}
                                        />
                                    </>
                                </Tooltip>
                            </div>
                            <div className="col-md-3 col-12 mb-2">
                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                    <>
                                        <DropdownField
                                            label={t("19", "All Languages")}
                                            hasOptions={true}
                                            options={languageOptions}
                                            plain={true}
                                            size={"small"}
                                            multiple={true}
                                            selectAll={true}
                                            value={filters.lang_id ? filters.lang_id : []}
                                            onChange={(value) => navigationFunction("lang_id", value)}
                                        />
                                    </>
                                </Tooltip>
                            </div>
                            <div className="col-md-3 col-12 mb-2">
                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                    <>
                                        <DropdownField
                                            label={t("191", "All Expertise")}
                                            hasOptions={true}
                                            options={expertiseOptions}
                                            plain={true}
                                            size={"small"}
                                            multiple={true}
                                            selectAll={true}
                                            value={filters.expertise ? filters.expertise : []}
                                            onChange={(value) => navigationFunction("expertise", value)}
                                        />
                                    </>
                                </Tooltip>
                            </div>
                            <div className="col-md-3 col-12 mb-2">
                                <Tooltip title={t("667", "You can select multiple options")} placement="top" arrow>
                                    <>
                                        <DropdownField
                                            label={t("296", "Expert Name")}
                                            hasOptions={true}
                                            options={expertNameOptions}
                                            plain={true}
                                            size={"small"}
                                            multiple={true}
                                            selectAll={true}
                                            value={filters.expert_name ? filters.expert_name : []}
                                            onChange={(value) => navigationFunction("expert_name", value)}
                                        />
                                    </>
                                </Tooltip>
                            </div>
                            <div className="col-md-9 col-sm-6 col-12 mt-2 text-end">
                                <button
                                    className="btn btn--outline fs-7 lh-1 mb-2 ms-2"
                                    type="button"
                                    onClick={() => resetFilter()}
                                >
                                    <span className="align-middle">{t("258", "Reset")}</span>
                                    <i className="fas fa-arrow-rotate-left text-black ms-2" />
                                </button>
                                <button
                                    className="btn btn--primary fs-7 lh-0 mb-2 ms-1"
                                    type="button"
                                    onClick={() => loadExperts()}
                                >
                                    <span className="align-middle">{t("21", "Search")}</span>
                                    <i className="fas fa-search text-white ms-2 " />
                                </button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Box mb={2}>
                    <DataTable
                        disabled={isSaving}
                        pageSize={5}
                        checkboxSelection={true}
                        onRowSelectionModelChange={handleRowSelection}
                        rowSelectionModel={rowSelectionModel}
                        // getRowId={(row) => `experts-${Math.random().toString(36).substr(2, 9)}-${row.id}`}
                        getRowId={(row) => row?.id ?? ""}
                        isLoading={isLoading}
                        rows={experts}
                        className="text-white bg-primary rounded-0"
                        columns={[
                            {
                                field: "first_name",
                                headerName: t("192", "First Name"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 100,
                                flex: 1,
                            },
                            {
                                field: "last_name",
                                headerName: t("193", "Last Name"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 100,
                                flex: 1,
                            },
                            {
                                field: "missions",
                                headerName: t("-1", "Missions"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 350,
                                flex: 0,
                                // renderCell: (params) => {
                                //     return (
                                //         <div className="row">
                                //             {Array.isArray(params?.row?.mission) ? params?.row?.mission.map((i, j) => {
                                //                 return <span className="col-12">{i.name}, </span>;
                                //             }) : ""
                                //             }
                                //         </div>
                                //     );
                                // }
                                renderCell: (params)=>{
                                    return (<MissionCellRenderer row={params.row} />)
                                }
                            },
                            {
                                field: "country_name",
                                headerName: t("2917", "Nationality"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                            },
                            {
                                field: "language",
                                headerName: t("26", "Language"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                                valueGetter: (params) => params.row.languages.map((i) => i.name).join(","),
                            },
                            {
                                field: "expert_areas",
                                headerName: t("40", "Expertise"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                                renderCell: (params) => (
                                    <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                                        {params.row.expert_areas.map((area, index) => (
                                            <div className="col-6 mb-1" key={`${index}-${area}`}>
                                                <span
                                                    className={`badge rounded-3 p-2 ${area === "PVS Expert" && "bg-green"
                                                        } ${area === "Aqua Expert" && "bg-light-blue"} ${area === "Gap Expert" && "bg-light-orange"
                                                        } ${area === "Legislation Expert" && "bg-light-alice-blue"} ${area === "Laboratory Expert" && "bg-light-gray"
                                                        }`}
                                                    style={{ fontSize: "0.875rem" }}
                                                >
                                                    <div key={index}>{area}</div>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Box>
                <Box mb={2}>
                    <h2 className="modal-title mb-2">{`Peer Reviewers of Previous Missions - ${MissionCountryName} `}</h2>
                    {/* Previously Mission Expert */}
                    <DataTable
                        disabled={isSaving}
                        pageSize={5}
                        checkboxSelection={true}
                        onRowSelectionModelChange={handleRowSelection}
                        rowSelectionModel={rowSelectionModel}
                        // getRowId={(row) => `experts-${Math.random().toString(36).substr(2, 9)}-${row.id}`}
                        getRowId={(row) => row.id ?? ""}
                        isLoading={isLoading}
                        rows={previousMissionExperts}
                        className="text-white bg-primary rounded-0"
                        columns={[
                            {
                                field: "first_name",
                                headerName: t("192", "First Name"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 100,
                                flex: 1,
                            },
                            {
                                field: "last_name",
                                headerName: t("193", "Last Name"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 100,
                                flex: 1,
                            },
                            {
                                field: "missions",
                                headerName: t("-1", "Missions"),
                                headerClassName: "text-white bg-primary rounded-0",
                                minWidth: 350,
                                flex: 0,
                                renderCell: (params) => {
                                    return (
                                        <div className="row">
                                            {Array.isArray(params?.row?.mission) ? params?.row?.mission.map((i, j) => {
                                                return <span className="col-12">{i.name}, </span>;
                                            }) : ""
                                            }
                                        </div>
                                    );
                                }
                            },
                            {
                                field: "country_name",
                                headerName: t("2917", "Nationality"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                            },
                            {
                                field: "language",
                                headerName: t("26", "Language"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                                valueGetter: (params) => params.row.languages.map((i) => i.name).join(","),
                            },
                            {
                                field: "expert_areas",
                                headerName: t("40", "Expertise"),
                                headerClassName: "text-white bg-primary rounded-0",
                                flex: 1,
                                renderCell: (params) => (
                                    <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                                        {params.row.expert_areas.map((area, index) => (
                                            <div className="col-6 mb-1" key={`${index}-${area}`}>
                                                <span
                                                    className={`badge rounded-3 p-2 ${area === "PVS Expert" && "bg-green"
                                                        } ${area === "Aqua Expert" && "bg-light-blue"} ${area === "Gap Expert" && "bg-light-orange"
                                                        } ${area === "Legislation Expert" && "bg-light-alice-blue"} ${area === "Laboratory Expert" && "bg-light-gray"
                                                        }`}
                                                    style={{ fontSize: "0.875rem" }}
                                                >
                                                    <div key={index}>{area}</div>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Box>
                {rowSelectionModel.length > 0 ? (
                    <Box className="bg-light-3 p-4" mb={2}>
                        <ol>
                            {rowSelectionModel.map((r) => {
                                const expert = experts.find((s) => String(s.id) === String(r));
                                return (
                                    <li key={_.uniqueId(expert?.id + '-')}>
                                        <div className="row mb-2">
                                            <div className="col-md-6 col-sm-6 col-12 d-flex">
                                                <div className="my-auto">
                                                    {expert?.first_name} {expert?.last_name}
                                                </div>
                                            </div>
                                            <div className="col-md-4 col-sm-4 col-12">
                                                <DropdownField
                                                    label={"Select Position"}
                                                    value={data[r]}
                                                    onChange={(val) => setData({ ...data, [r]: val })}
                                                    hasOptions={true}
                                                    options={positionsOptions}
                                                    plain={true}
                                                    size={"small"}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                        <div className="d-flex">
                            <div className="ms-auto">
                                <Button variant="contained" color="success" onClick={handleSave} disabled={isSaving}>
                                    <FaCheck />
                                    <span className="ms-1">{isSaving ? "Saving..." : "Save"}</span>
                                </Button>
                            </div>
                        </div>
                    </Box>
                ) : (
                    <Box mb={2}>
                        <Alert severity="info">
                            No Experts selected. Please select to continue
                        </Alert>
                    </Box>
                )}
            </>
        </PopupModal>
    );
};

export default ManagePeerReviewerModal;
